import { Injectable } from '@angular/core';
import { Credentials } from '../../shared/objects/auth/credentials';
import { UserInfo } from '../../shared/objects/auth/userinfo';
import { AuthConstants } from '../../shared/const/authConstants';
import { environment } from '../../../environments/environment';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { UserRole } from '../../shared/objects/auth/userRole';
import { LoginStatusEnum } from '../../shared/objects/enum/loginStatus';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { HttpResponseStatusCode } from '../../shared/objects/enum/httpResponseCode';
import { Module } from '../../shared/objects/auth/module';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserInactivityService } from './user-inactivity.service';

import { AuthConfig } from 'angular-oauth2-oidc';
import { AdminEmails } from '../../shared/components/filter-menu/models/admin-emails';
import { LoginAttempt } from '../../shared/objects/general/login-attempt.model';
import { LoginStatusModel } from '../../shared/objects/general/login-status.model';
import jwtDecode from 'jwt-decode';

const authCodeFlowConfig: AuthConfig = {
  issuer: environment.keycloakRealmUrl,
  redirectUri: '',
  clientId: environment.keycloakClient,
  responseType: 'code',
  scope: 'openid',
  showDebugInformation: true,
  dummyClientSecret: environment.keycloakClientSecret,
  requireHttps: environment.useHttps,
  oidc: false,
  timeoutFactor: 0.98,
};

@Injectable()
export class AuthService {
  private sourceTimeoutStatus = new BehaviorSubject<boolean>(false);
  private oauthEventsSubscription: Subscription;
  private userActivitySubscription: Subscription;
  userIsLogged = new BehaviorSubject<boolean>(false);

  timeoutStatus = this.sourceTimeoutStatus.asObservable();
  public config = authCodeFlowConfig;

  constructor(
    private httpClient: HttpClient,
    private oauthService: OAuthService,
    private inactivityService: UserInactivityService,
  ) {}
  isImpersonated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    const tokenDecoded = jwtDecode(token);
    return tokenDecoded['impersonator_username']?.length > 0;
  }

  init() {
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.setStorage(localStorage);
    this.oauthService.loadDiscoveryDocument();
  }

  public updateUserLoginState(status: boolean): void {
    this.userIsLogged.next(status);
  }
  login(credentials: Credentials): Promise<LoginStatusModel> {
    return this.loginWithOAuth(credentials)
      .then((response: LoginStatusModel) => {
        return this.resetLoginAttempt(credentials.username).then(r => {
          if (r === true) {
            const loginAttempt: LoginStatusModel = {
              attempts: null,
              status: LoginStatusEnum.PASSWORD_RESET,
            };
            return loginAttempt;
          } else {
            this.userIsLogged.next(true);
            return response;
          }
        });
      })
      .catch<LoginStatusModel>(responseError => {
        const loginStatus: LoginStatusModel = {
          attempts: null,
          status: null,
        };

        switch (responseError.status) {
          case HttpResponseStatusCode.Unauthorized:
            return this.processLoginAttempt(credentials.username)
              .then(r => {
                loginStatus.attempts = r.attempts;
                loginStatus.status =
                  r.shouldReset == true
                    ? LoginStatusEnum.PASSWORD_RESET
                    : LoginStatusEnum.INVALID_CREDENTIALS;

                return loginStatus;
              })
              .catch(() => {
                loginStatus.status = LoginStatusEnum.INVALID_CREDENTIALS;
                return loginStatus;
              });
          case HttpResponseStatusCode.BadRequest:
            loginStatus.status = LoginStatusEnum.BLOCKED;
            return loginStatus;

          default:
            loginStatus.status = LoginStatusEnum.ERROR;
            return loginStatus;
        }
      });
  }

  private processLoginAttempt(username: string): Promise<LoginAttempt> {
    const headers = this.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<LoginAttempt>(
        `${environment.anonymousServiceUrl}/loginAttempt/${username}`,
        { headers },
      )
      .toPromise();
  }

  loginWithOAuth(credentials: Credentials): Promise<LoginStatusModel> {
    const loginAttempt: LoginStatusModel = {
      attempts: null,
      status: LoginStatusEnum.SUCCESS,
    };
    return this.oauthService
      .fetchTokenUsingPasswordFlow(credentials.username, credentials.password)
      .then(() => {
        return this.oauthService
          .loadUserProfile()
          .then(profile => {
            const userInfo = profile['info'] as UserInfo;
            const role = this.getUserRole();

            if (
              role === UserRole.BusinessAdmin &&
              !userInfo.business_unit_urls?.includes(
                this.getCurrentBusinessUnitUrl(),
              )
            ) {
              this.oauthService.logOut();
              loginAttempt.status = LoginStatusEnum.INVALID_CREDENTIALS;
              return loginAttempt;
            }

            this.setupAccessTokenTimeoutContext();

            loginAttempt.status = LoginStatusEnum.SUCCESS;
            return loginAttempt;
          })
          .catch(() => {
            loginAttempt.status = LoginStatusEnum.ERROR;

            return loginAttempt;
          });
      });
  }

  getCurrentBusinessUnitUrl(): string {
    for (const el of environment.businessunits) {
      if (window.location.host === el.url) {
        return el.url;
      }
    }
    return undefined;
  }

  logout() {
    sessionStorage.clear();
    this.clearSubscriptions();
    this.inactivityService.clearEventListeners();
    this.inactivityService.clearTimeouts();
    this.userIsLogged.next(false);
    // no redirect url after logout
    this.oauthService.logOut(true);
  }

  getBearerRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', 'bearer ' + this.getToken());
  }

  getBearerRequestHeadersAnonymous(): HttpHeaders {
    return new HttpHeaders().set('Authorization', 'bearer');
  }

  getToken(): string {
    return this.oauthService.getAccessToken();
  }

  getUserInfo(): UserInfo {
    return this.oauthService.getIdentityClaims() as UserInfo;
  }

  getStoredUserInfo(): UserInfo {
    return this.oauthService.getIdentityClaims() as UserInfo;
  }

  getUserRole(): UserRole {
    const token = this.getToken();
    if (!token) {
      return UserRole.Unknown;
    }
    const tokenDecoded = jwtDecode(token);
    const roles = tokenDecoded['resource_access']['webapp']['roles'];
    return this.resolveRole(roles);
  }

  private resolveRole(roles: string[]): UserRole {
    while (roles && roles.length !== 0) {
      const role = roles.pop();
      switch (role) {
        case AuthConstants.CARRIER_ADMIN_KEYCLOAK_KEY:
          return UserRole.CarrierAdmin;
        case AuthConstants.CARRIER_USER_KEYCLOAK_KEY:
          return UserRole.CarrierUser;
        case AuthConstants.PORTAL_ADMIN_KEYCLOAK_KEY:
          return UserRole.PortalAdmin;
        case AuthConstants.BUSINESS_ADMIN_KEYCLOAK_KEY:
          return UserRole.BusinessAdmin;
        default:
          break;
      }
    }
    return UserRole.Unknown;
  }

  getUserFeatureClaims(): Module[] {
    const userInfo: UserInfo =
      this.oauthService.getIdentityClaims() as UserInfo;
    let featureClaims: Module[] = [];
    if (userInfo) {
      const featureModules: string[] = userInfo.enabled_modules;

      if (featureModules) {
        featureClaims = featureModules.map<Module>(mod => Module[mod]);
      }
    }
    return featureClaims;
  }

  getUserAdministrationClaims(): Module[] {
    if (!this.oauthService.hasValidAccessToken()) {
      return null;
    }
    const userInfo: UserInfo =
      this.oauthService.getIdentityClaims() as UserInfo;
    let administrationClaims: Module[] = [];
    if (userInfo) {
      const administrationModules: string[] = userInfo.administration_modules;

      if (administrationModules) {
        administrationClaims = administrationModules.map<Module>(
          mod => Module[mod],
        );
      }
    }
    return administrationClaims;
  }

  userHasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  private refreshAccessToken(): void {
    this.oauthService.refreshToken();
  }

  public setupAccessTokenTimeoutContext(): void {
    this.oauthEventsSubscription = this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
      } else {
        switch (event.type) {
          case 'token_expires':
            this.refreshAccessToken();
            break;
          default:
            break;
        }
      }
    });
  }

  public setupUserInactivityTimeoutContext(): void {
    this.inactivityService.setupUserActivityListener();
    this.userActivitySubscription =
      this.inactivityService.userActivity.subscribe(userIsInactive => {
        if (userIsInactive) {
          this.logout();
          this.sourceTimeoutStatus.next(userIsInactive);
        } else {
          this.refreshAccessToken();
        }
      });
  }

  private clearSubscriptions(): void {
    if (!!this.oauthEventsSubscription) {
      this.oauthEventsSubscription.unsubscribe();
    }
    if (!!this.userActivitySubscription) {
      this.userActivitySubscription.unsubscribe();
    }
  }
  public getUserAdminsEmails(username: string): Observable<AdminEmails> {
    return this.httpClient.get<AdminEmails>(
      `/api/usersync/getUserAdminEmails/${username}`,
    );
  }

  private resetLoginAttempt(username: string): Promise<boolean> {
    const headers = this.getBearerRequestHeadersAnonymous();
    return this.httpClient
      .get<boolean>(
        `${environment.anonymousServiceUrl}/resetAttempts/${username}`,
        { headers },
      )
      .toPromise();
  }

  isCurrentUser(username: string | undefined): boolean {
    const userInfo = this.getUserInfo();
    return (
      userInfo?.preferred_username.toLowerCase() === username?.toLowerCase()
    );
  }
}
